import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import api from '../../services/api';

const JokesManager = () => {
    const { showToast } = useApp();
    const [jokes, setJokes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingJoke, setEditingJoke] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        title: '',  // ✅ Added title field
        text: '',
        category: 'general',
        type: 'one-liner',
        setup: '',
        punchline: '',
        image: '',
        imageData: '',
        status: 'published',
        featured: false,
        submittedBy: 'Admin',
        seo: {
            metaTitle: '',
            metaDescription: '',
            metaKeywords: ''
        }
    });

    useEffect(() => {
        fetchJokes();
    }, []);

    const fetchJokes = async () => {
        try {
            const res = await api.get('/jokes?status=all');
            const jokesData = res.data.jokes || res.data;
            setJokes(Array.isArray(jokesData) ? jokesData : []);
        } catch (error) {
            console.error('Error fetching jokes:', error);
            setJokes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.title) {
            showToast('Please enter a joke title', 'error');
            return;
        }
        if (!formData.text) {
            showToast('Please enter the joke text', 'error');
            return;
        }

        try {
            if (editingJoke) {
                await api.put(`/jokes/${editingJoke._id}`, formData);
                showToast('Joke updated successfully!');
            } else {
                await api.post('/jokes', formData);
                showToast('Joke added successfully!');
            }
            await fetchJokes();
            resetForm();
        } catch (error) {
            console.error('Error saving joke:', error);
            showToast('Error saving joke', 'error');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this joke?')) {
            try {
                await api.delete(`/jokes/${id}`);
                await fetchJokes();
                showToast('Joke deleted successfully');
            } catch (error) {
                showToast('Error deleting joke', 'error');
            }
        }
    };

    const handleLike = async (id) => {
        try {
            await api.post(`/jokes/${id}/like`);
            await fetchJokes();
        } catch (error) {
            console.error('Error liking joke:', error);
        }
    };

    const resetForm = () => {
        setEditingJoke(null);
        setShowForm(false);
        setFormData({
            title: '',
            text: '',
            category: 'general',
            type: 'one-liner',
            setup: '',
            punchline: '',
            image: '',
            imageData: '',
            status: 'published',
            featured: false,
            submittedBy: 'Admin',
            seo: {
                metaTitle: '',
                metaDescription: '',
                metaKeywords: ''
            }
        });
    };

    const editJoke = (joke) => {
        setEditingJoke(joke);
        setFormData(joke);
        setShowForm(true);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSEOChange = (e) => {
        setFormData({
            ...formData,
            seo: { ...formData.seo, [e.target.name]: e.target.value }
        });
    };

    if (loading) {
        return <div className="loader"><div className="loader-spinner"></div></div>;
    }

    return (
        <div>
            <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', marginBottom: '22px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0 }}>😂 Jokes Manager</h3>
                    <button className="btn btn-red" onClick={() => setShowForm(!showForm)}>
                        {showForm ? 'Cancel' : '+ Add New Joke'}
                    </button>
                </div>

                {showForm && (
                    <form onSubmit={handleSubmit} style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <h4>{editingJoke ? '✏️ Edit Joke' : '➕ Add New Joke'}</h4>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                            {/* ✅ Title Field - New */}
                            <div className="fg" style={{ gridColumn: '1/-1' }}>
                                <label>📝 Joke Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                    placeholder="e.g., Why did the chicken cross the road?"
                                />
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    A catchy title helps users find your joke (Recommended: 50-60 characters)
                                </div>
                            </div>

                            {/* ✅ Joke Text Field */}
                            <div className="fg" style={{ gridColumn: '1/-1' }}>
                                <label>😂 Joke Content *</label>
                                <textarea
                                    name="text"
                                    value={formData.text}
                                    onChange={handleChange}
                                    required
                                    rows="4"
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                    placeholder="Enter the full joke here..."
                                />
                            </div>

                            <div className="fg">
                                <label>📂 Category</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                >
                                    <option value="general">😂 General</option>
                                    <option value="programming">💻 Programming</option>
                                    <option value="dad">👨 Dad Jokes</option>
                                    <option value="chuck">🥋 Chuck Norris</option>
                                </select>
                            </div>

                            <div className="fg">
                                <label>📌 Status</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                >
                                    <option value="published">📢 Published</option>
                                    <option value="draft">📝 Draft</option>
                                </select>
                            </div>

                            <div className="fg">
                                <label>⭐ Featured</label>
                                <select
                                    name="featured"
                                    value={formData.featured}
                                    onChange={(e) => setFormData({ ...formData, featured: e.target.value === 'true' })}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                >
                                    <option value="false">No</option>
                                    <option value="true">⭐ Yes (Show on homepage)</option>
                                </select>
                            </div>

                            <div className="fg">
                                <label>👤 Submitted By</label>
                                <input
                                    type="text"
                                    name="submittedBy"
                                    value={formData.submittedBy}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                />
                            </div>

                            {/* ✅ Image Upload Section */}
                            <div className="fg full">
                                <label>🖼️ Joke Image (Optional)</label>
                                <input
                                    type="text"
                                    name="image"
                                    value={formData.image}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                    placeholder="https://example.com/joke-image.jpg"
                                />
                                <div style={{ marginTop: '10px' }}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                if (file.size > 5 * 1024 * 1024) {
                                                    showToast('Image too large. Max 5MB', 'error');
                                                    return;
                                                }
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setFormData({ ...formData, imageData: reader.result, image: '' });
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        style={{ padding: '8px' }}
                                    />
                                    <p style={{ fontSize: '11px', color: '#888', marginTop: '5px' }}>
                                        Or upload an image (JPG, PNG, GIF) - Max 5MB
                                    </p>
                                </div>
                                {(formData.image || formData.imageData) && (
                                    <div style={{ marginTop: '10px' }}>
                                        <img src={formData.image || formData.imageData} alt="Preview" style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }} />
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, image: '', imageData: '' })}
                                            style={{ marginTop: '5px', padding: '2px 10px', fontSize: '11px', background: '#ff6b6b', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                        >
                                            Remove Image
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* ✅ SEO Section */}
                            <div className="fg" style={{ gridColumn: '1/-1' }}>
                                <details>
                                    <summary style={{ cursor: 'pointer', fontWeight: '600', color: 'var(--navy)' }}>🔍 SEO Settings (Optional)</summary>
                                    <div style={{ marginTop: '15px' }}>
                                        <div className="fg" style={{ marginBottom: '10px' }}>
                                            <label>Meta Title</label>
                                            <input
                                                type="text"
                                                name="metaTitle"
                                                value={formData.seo?.metaTitle || ''}
                                                onChange={handleSEOChange}
                                                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                                placeholder="SEO title (optional)"
                                            />
                                        </div>
                                        <div className="fg full">
                                            <label>🔗 SEO URL Slug</label>
                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <div style={{ flex: 1 }}>
                                                    <input
                                                        type="text"
                                                        name="slug"
                                                        value={formData.slug || ''}
                                                        onChange={handleSlugChange}
                                                        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                                        placeholder="auto-generated-from-title"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={autoGenerateSlug}
                                                    className="btn btn-outline"
                                                    style={{ padding: '5px 15px', whiteSpace: 'nowrap' }}
                                                >
                                                    🔄 Auto-Generate
                                                </button>
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                                💡 SEO-friendly URL: wistarnetwork.com/joke/<strong>{formData.slug || 'your-url-slug'}</strong>
                                            </div>
                                        </div>
                                        <div className="fg" style={{ marginBottom: '10px' }}>
                                            <label>Meta Description</label>
                                            <textarea
                                                name="metaDescription"
                                                value={formData.seo?.metaDescription || ''}
                                                onChange={handleSEOChange}
                                                rows="2"
                                                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                                placeholder="SEO description (optional)"
                                            />
                                        </div>
                                        <div className="fg">
                                            <label>Meta Keywords</label>
                                            <input
                                                type="text"
                                                name="metaKeywords"
                                                value={formData.seo?.metaKeywords || ''}
                                                onChange={handleSEOChange}
                                                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                                placeholder="keyword1, keyword2, keyword3"
                                            />
                                        </div>

                                    </div>
                                </details>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <button type="submit" className="btn btn-red">{editingJoke ? '💾 Update Joke' : '💾 Save Joke'}</button>
                            <button type="button" className="btn btn-outline" onClick={resetForm}>Cancel</button>
                        </div>
                    </form>
                )}

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: 'var(--navy)', color: '#fff' }}>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Image</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Title / Joke</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Category</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Likes</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Views</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Status</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jokes.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                        No jokes yet. Click "Add New Joke" to get started!
                                    </td>
                                </tr>
                            ) : (
                                jokes.map(joke => (
                                    <tr key={joke._id}>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                                            {(joke.image || joke.imageData) ? (
                                                <img
                                                    src={joke.image || joke.imageData}
                                                    alt=""
                                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            ) : (
                                                <div style={{ fontSize: '30px' }}>
                                                    {joke.category === 'programming' ? '💻' :
                                                        joke.category === 'dad' ? '👨' :
                                                            joke.category === 'chuck' ? '🥋' : '😂'}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', maxWidth: '350px' }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{joke.title || 'Untitled Joke'}</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>{joke.text.substring(0, 60)}...</div>
                                        </td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{joke.category}</td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                                            <button onClick={() => handleLike(joke._id)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                                ❤️ {joke.likes || 0}
                                            </button>
                                        </td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>{joke.views || 0}</td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                                            <span className={`badge ${joke.status === 'published' ? 'status-live' : 'status-draft'}`}>
                                                {joke.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                                            <button className="action-btn action-edit" onClick={() => editJoke(joke)}>Edit</button>
                                            <button className="action-btn action-del" onClick={() => handleDelete(joke._id)}>Delete</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default JokesManager;